import { useMediaQuery, useTheme } from '@mui/material';
import React from 'react';
import { ImperativePanelHandle } from 'react-resizable-panels';

import { SIDEBAR_PANEL_MIN_WIDTH } from 'src/constants/uiConsts';
import { useFeatureFlags } from 'src/context';
import { useDrawerWidth } from 'src/features/drawer/useDrawerWidth';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { RootState } from 'src/store/reduxTypes';
import { togglePrimarySidebarMobile } from 'src/store/ui/actions';

export interface ShellContextValue {
  drawerWidth: number;
  sidebarOpen: boolean;
  openMobileSidebar: () => void;
  closeMobileSidebar: () => void;
  isMobile: boolean;
  showContextBar: boolean;
  setShowContextBar: (show: boolean) => void;
  contextBarContentMinWidth: number | undefined;
  setContextBarContentMinWidth: (minWidth: number | undefined) => void;
  sidebarPanelRef: React.RefObject<ImperativePanelHandle | null>;
  onSidebarPanelResize: (sizePercent: number) => void;
  isResizableSidebarActive: boolean;
  setDisableResizableSidebar: (disabled: boolean) => void;
  isDesktopSidebarCollapsed: boolean;
  toggleDesktopSidebar: () => void;
  onSidebarCollapse: () => void;
  onSidebarExpand: () => void;
}

export const ShellContext = React.createContext<ShellContextValue | null>(null);

export const ShellProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { EnableNewSidebar } = useFeatureFlags();
  const [isResizableSidebarDisabled, setDisableResizableSidebar] =
    React.useState(false);
  const isResizableSidebarActive =
    EnableNewSidebar && !isMobile && !isResizableSidebarDisabled;

  const sidebarPanelRef = React.useRef<ImperativePanelHandle>(null);
  // Initialise with the default panel width so drawerWidth is non-zero on
  // the first render, before the ResizablePanel fires its onResize callback.
  const [sidebarPanelSizePercent, setSidebarPanelSizePercent] = React.useState(
    () =>
      typeof window !== 'undefined' && window.innerWidth > 0
        ? (SIDEBAR_PANEL_MIN_WIDTH / window.innerWidth) * 100
        : 0,
  );

  const onSidebarPanelResize = React.useCallback(
    (sizePercent: number) => setSidebarPanelSizePercent(sizePercent),
    [],
  );

  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] =
    React.useState(false);

  const onSidebarCollapse = React.useCallback(() => {
    setIsDesktopSidebarCollapsed(true);
  }, []);

  const onSidebarExpand = React.useCallback(() => {
    setIsDesktopSidebarCollapsed(false);
  }, []);

  const toggleDesktopSidebar = React.useCallback(() => {
    const panel = sidebarPanelRef.current;
    if (!panel) return;
    if (panel.isCollapsed()) {
      panel.expand();
    } else {
      panel.collapse();
    }
  }, [sidebarPanelRef]);

  const drawerWidth = useDrawerWidth(
    isResizableSidebarActive ? sidebarPanelSizePercent : null,
  );

  const sidebarOpen = useAppSelector(
    (state: RootState) => state.ui.primarySideBarMobile.isOpen,
  );

  const [showContextBar, setShowContextBar] = React.useState(false);
  const [contextBarContentMinWidth, setContextBarContentMinWidth] =
    React.useState<number | undefined>(undefined);

  // Dispatch the desired state directly to avoid stale-closure issues
  // that occur with a toggle pattern (where the callback captures sidebarOpen).
  const openMobileSidebar = React.useCallback(() => {
    dispatch(togglePrimarySidebarMobile({ isOpen: true }));
  }, [dispatch]);

  const closeMobileSidebar = React.useCallback(() => {
    dispatch(togglePrimarySidebarMobile({ isOpen: false }));
  }, [dispatch]);

  const value = React.useMemo<ShellContextValue>(
    () => ({
      drawerWidth,
      sidebarOpen,
      openMobileSidebar,
      closeMobileSidebar,
      isMobile,
      showContextBar,
      setShowContextBar,
      contextBarContentMinWidth,
      setContextBarContentMinWidth,
      sidebarPanelRef,
      onSidebarPanelResize,
      isResizableSidebarActive,
      setDisableResizableSidebar,
      isDesktopSidebarCollapsed,
      toggleDesktopSidebar,
      onSidebarCollapse,
      onSidebarExpand,
    }),
    [
      drawerWidth,
      sidebarOpen,
      openMobileSidebar,
      closeMobileSidebar,
      isMobile,
      showContextBar,
      contextBarContentMinWidth,
      sidebarPanelRef,
      onSidebarPanelResize,
      isResizableSidebarActive,
      setDisableResizableSidebar,
      isDesktopSidebarCollapsed,
      toggleDesktopSidebar,
      onSidebarCollapse,
      onSidebarExpand,
    ],
  );

  return (
    <ShellContext.Provider value={value}>{children}</ShellContext.Provider>
  );
};

export function useShell(): ShellContextValue {
  const context = React.useContext(ShellContext);
  if (context === null) {
    throw new Error('useShell must be used within a ShellProvider');
  }
  return context;
}
